import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Chip,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import ReplayIcon from "@material-ui/icons/Replay";
import FunctionsIcon from "@material-ui/icons/Functions";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import WarningIcon from "@material-ui/icons/Warning";
import SaveIcon from "@material-ui/icons/Save";
import LinkIcon from "@material-ui/icons/Link";

import ITEM_CFT_MAPPING, { ITEM_CFT_OPTIONS } from "../../constants/itemCftMapping";
import {
  calculateGrandTotal,
  calculateRowCft,
  formatCft,
  getCftValueForItem,
  getTotalQuantity,
  sanitizeQuantity,
} from "../../utils/cftCalculator";
import { applyQuotationValidationToRow, validateQuotationRowsForSave } from "../../utils/quotationValidation";
import { suggestTruckFromCft } from "../../utils/suggestedTruckFromCft";
import axios from "../../services";
import toaster from "../../utils/toaster";

const useStyles = makeStyles((theme) => ({
  introBox: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "130px",
    marginTop: "60px",
    fontFamily: "Roboto Mono, monospace",
  },
  primaryText: {
    fontSize: "35px",
    fontWeight: "bold",
  },
  secondaryText: {
    position: "relative",
    top: "-10px",
    opacity: ".78",
  },
  actionBar: {
    marginTop: theme.spacing(2),
    marginLeft: "130px",
    display: "flex",
    gap: theme.spacing(1),
  },
  sectionCard: {
    marginLeft: "130px",
    marginTop: theme.spacing(2),
    width: "calc(100% - 190px)",
    maxWidth: 1200,
    padding: theme.spacing(2),
  },
  tableWrap: {
    marginLeft: "130px",
    marginTop: theme.spacing(2),
    width: "calc(100% - 190px)",
    maxWidth: 1200,
  },
  summaryCard: {
    marginTop: theme.spacing(2),
    width: 380,
  },
  itemInput: {
    minWidth: 260,
  },
  quantityInput: {
    width: 130,
  },
  cftInput: {
    width: 130,
  },
  warningPanel: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    background: "#fff8e1",
    border: "1px solid #ffe082",
  },
  importActions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    flexWrap: "wrap",
  },
  rowNeedsManualCft: {
    backgroundColor: "#fff3e0",
  },
}));

/** Unmatched line with quantity > 0 but no valid CFT yet — draw attention to fill CFT. */
function rowNeedsManualCftHighlight(row) {
  const qtyState = sanitizeQuantity(row.quantity);
  if (!qtyState.isValid || qtyState.value <= 0) return false;
  const cftN = Number(row.cftPerItem);
  const cftOk = row.cftPerItem !== "" && Number.isFinite(cftN) && cftN > 0;
  if (cftOk) return false;
  return row.matchStatus === "Unmatched";
}

const createEmptyRow = (id) => ({
  id,
  item: "",
  quantity: "",
  cftPerItem: "",
  totalCft: 0,
  quantityError: "",
  cftError: "",
  sourceMatched: false,
  normalizedItem: "",
  notes: "",
  matchStatus: "Unmatched",
});

/** Prefer local ITEM_CFT_MAPPING so amounts match what the user edits in the frontend, even if the API is stale. */
const buildRowFromImportItem = (item, id) => {
  const itemName = (item.item || "").trim();
  const clientMatch = getCftValueForItem(itemName, ITEM_CFT_MAPPING);
  const qtyStr =
    item.quantity === "" || item.quantity === null || item.quantity === undefined
      ? ""
      : String(item.quantity);
  const qtyState = sanitizeQuantity(qtyStr);

  let cftPerItem = "";
  let sourceMatched = false;
  let matchStatus = "Unmatched";
  let normalizedItem = item.normalizedItem || clientMatch.normalizedItem || "";

  if (clientMatch.matched && typeof clientMatch.cftPerItem === "number") {
    cftPerItem = clientMatch.cftPerItem;
    sourceMatched = true;
    matchStatus = "Matched";
    normalizedItem = clientMatch.normalizedItem || normalizedItem;
  } else {
    const apiCft = item.cftPerItem;
    const n = Number(apiCft);
    if (
      apiCft !== "" &&
      apiCft !== null &&
      apiCft !== undefined &&
      Number.isFinite(n) &&
      n > 0
    ) {
      cftPerItem = n;
      sourceMatched = !!item.matched;
      matchStatus = item.matched ? "Matched" : "Manually Updated";
    }
  }

  const quantity = qtyState.isValid ? qtyState.value : 0;
  const cftNum = Number(cftPerItem);
  const cftValid = cftPerItem !== "" && Number.isFinite(cftNum) && cftNum >= 0;
  const totalCft =
    qtyState.isValid && cftValid ? calculateRowCft(quantity, cftNum) : 0;

  return applyQuotationValidationToRow({
    id,
    item: itemName,
    quantity: qtyStr,
    cftPerItem: cftPerItem === "" ? "" : cftPerItem,
    totalCft,
    quantityError: "",
    cftError: "",
    sourceMatched,
    normalizedItem,
    notes: "",
    matchStatus,
  });
};

const CftCalculatorPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { quotationId } = useParams();
  const isEditing = !!quotationId;
  const [rows, setRows] = useState([createEmptyRow(1)]);
  const [nextId, setNextId] = useState(2);
  const [file, setFile] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [importSummary, setImportSummary] = useState({
    extractedRows: 0,
    matchedItems: 0,
    unmatchedItems: 0,
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingImportData, setPendingImportData] = useState(null);
  const [customer, setCustomer] = useState({
    customerName: "",
    email: "",
    phoneNumber: "",
    companyName: "",
    preferredMovingDate: "",
    originAddress1: "",
    originAddress2: "",
    originCity: "",
    originState: "",
    originPincode: "",
    destinationAddress1: "",
    destinationAddress2: "",
    destinationCity: "",
    destinationState: "",
    destinationPincode: "",
  });
  const [loadingQuotation, setLoadingQuotation] = useState(false);
  /** Separate from household/office lines: descriptions only, no qty/CFT. */
  const [vehicles, setVehicles] = useState([""]);

  const updateRow = (rowId, updater) => {
    setRows((prev) => prev.map((row) => (row.id === rowId ? updater(row) : row)));
  };

  const handleItemChange = (rowId, itemName) => {
    updateRow(rowId, (row) => {
      const match = getCftValueForItem(itemName, ITEM_CFT_MAPPING);
      const qtyState = sanitizeQuantity(row.quantity);
      const quantity = qtyState.isValid ? qtyState.value : 0;
      const cftPerItem = match.matched ? match.cftPerItem : "";
      const next = {
        ...row,
        item: itemName,
        normalizedItem: match.normalizedItem,
        sourceMatched: match.matched,
        matchStatus: match.matched ? "Matched" : row.cftPerItem ? "Manually Updated" : "Unmatched",
        cftPerItem,
        totalCft: calculateRowCft(quantity, cftPerItem),
      };
      return applyQuotationValidationToRow(next);
    });
  };

  const handleQuantityChange = (rowId, value) => {
    updateRow(rowId, (row) => {
      const qtyState = sanitizeQuantity(value);
      const quantity = qtyState.isValid ? qtyState.value : 0;
      const cftNum = Number(row.cftPerItem);
      const cftOk = row.cftPerItem !== "" && Number.isFinite(cftNum) && cftNum >= 0;
      const next = {
        ...row,
        quantity: value,
        totalCft: qtyState.isValid && cftOk ? calculateRowCft(quantity, cftNum) : 0,
      };
      return applyQuotationValidationToRow(next);
    });
  };

  const handleCftChange = (rowId, value) => {
    updateRow(rowId, (row) => {
      const parsed = Number(value);
      const hasFormatError = value !== "" && (!Number.isFinite(parsed) || parsed < 0);
      const qtyState = sanitizeQuantity(row.quantity);
      const next = {
        ...row,
        cftPerItem: value,
        totalCft:
          !hasFormatError && qtyState.isValid
            ? calculateRowCft(qtyState.value, parsed)
            : 0,
        matchStatus:
          row.sourceMatched && !hasFormatError && value !== ""
            ? "Matched"
            : value === ""
            ? "Unmatched"
            : "Manually Updated",
      };
      const validated = applyQuotationValidationToRow(next);
      if (hasFormatError) {
        return { ...validated, cftError: "Enter valid non-negative CFT" };
      }
      return validated;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, createEmptyRow(nextId)]);
    setNextId((prev) => prev + 1);
  };

  const removeRow = (rowId) => {
    setRows((prev) => {
      const remaining = prev.filter((row) => row.id !== rowId);
      return remaining.length ? remaining : [createEmptyRow(nextId)];
    });
    if (rows.length === 1) {
      setNextId((prev) => prev + 1);
    }
  };

  const resetRows = () => {
    setRows([createEmptyRow(1)]);
    setNextId(2);
    setVehicles([""]);
    setWarnings([]);
    setImportSummary({
      extractedRows: 0,
      matchedItems: 0,
      unmatchedItems: 0,
    });
  };

  const calculateAllRows = () => {
    setRows((prev) =>
      prev.map((row) => {
        const qtyState = sanitizeQuantity(row.quantity);
        const quantity = qtyState.isValid ? qtyState.value : 0;
        const cftNum = Number(row.cftPerItem);
        const cftValid = row.cftPerItem !== "" && Number.isFinite(cftNum) && cftNum >= 0;
        const next = {
          ...row,
          totalCft: qtyState.isValid && cftValid ? calculateRowCft(quantity, cftNum) : 0,
        };
        const validated = applyQuotationValidationToRow(next);
        if (!cftValid && row.cftPerItem !== "") {
          return { ...validated, cftError: validated.cftError || "Enter valid non-negative CFT" };
        }
        return validated;
      })
    );
  };

  const hasData = useMemo(() => {
    const hasRows = rows.some((row) => row.item || row.quantity || row.cftPerItem);
    const hasCustomer = Object.values(customer).some((v) => v);
    const hasVehicles = vehicles.some((v) => v && String(v).trim());
    return hasRows || hasCustomer || hasVehicles;
  }, [rows, customer, vehicles]);

  const getPayloadRows = (rowsToUse = rows) => {
    return rowsToUse
      .filter((row) => row.item && row.item.trim())
      .map((row) => {
        const qty = Number(row.quantity);
        const quantity = Number.isFinite(qty) && qty >= 0 ? Math.max(0, Math.floor(qty)) : 0;
        const rawCft = row.cftPerItem;
        const cftPerItem =
          rawCft === "" || rawCft === null || rawCft === undefined
            ? 0
            : Math.max(0, Number(rawCft) || 0);
        return {
          itemName: row.item.trim(),
          normalizedItemName: row.normalizedItem,
          quantity,
          cftPerItem,
          matched: !!row.sourceMatched,
          matchStatus: row.matchStatus || "Unmatched",
          rawImportedItemName: row.item,
          notes: row.notes || "",
        };
      });
  };

  const copyCustomerIntakeLink = async () => {
    if (!isEditing || !quotationId) {
      toaster("Save the quotation first, then you can copy a customer link.", { type: "error" });
      return;
    }
    try {
      const res = await axios.post(`quotations/${quotationId}/public-link`);
      const t = res.data?.token;
      if (!t) {
        toaster("Could not create link", { type: "error" });
        return;
      }
      const url = `${window.location.origin}/public/cft/${t}`;
      await navigator.clipboard.writeText(url);
      toaster("Customer intake link copied to clipboard");
    } catch (err) {
      toaster(err?.response?.data?.message || "Failed to create public link", { type: "error" });
    }
  };

  const saveQuotation = async () => {
    if (!customer.customerName.trim()) {
      toaster("Customer name is required", { type: "error" });
      return;
    }
    const { rows: validatedRows, valid, firstInvalidId } = validateQuotationRowsForSave(rows);
    setRows(validatedRows);
    if (!valid) {
      toaster("Enter CFT for every line where quantity is greater than 0 before saving.", { type: "error" });
      const el = document.getElementById(`quotation-row-${firstInvalidId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const payloadRows = getPayloadRows(validatedRows);
    const vehicleList = vehicles.map((v) => String(v).trim()).filter(Boolean);
    if (!payloadRows.length && !vehicleList.length) {
      toaster("Add at least one inventory row or one vehicle", { type: "error" });
      return;
    }

    const payload = {
      ...customer,
      vehicles: vehicleList,
      items: payloadRows,
      totalItemsCount: summary.totalItemsCount,
      totalQuantity: summary.totalQuantity,
      totalCft: summary.grandTotal,
      importSourceFileName: file ? file.name : "",
    };
    try {
      if (isEditing) {
        const res = await axios.put(`quotations/${quotationId}`, payload);
        toaster(res.data?.message || "Quotation updated successfully");
      } else {
        const res = await axios.post("quotations", payload);
        toaster(res.data?.message || "Quotation created successfully");
      }
      history.push("/quotations");
    } catch (err) {
      toaster(err?.response?.data?.message || "Failed to save quotation", { type: "error" });
    }
  };

  useEffect(() => {
    const loadQuotation = async () => {
      if (!isEditing) return;
      setLoadingQuotation(true);
      try {
        const response = await axios.get(`quotations/${quotationId}`);
        const { quotation, items } = response.data;
        setCustomer({
          customerName: quotation.customerName || "",
          email: quotation.email || "",
          phoneNumber: quotation.phoneNumber || "",
          companyName: quotation.companyName || "",
          preferredMovingDate: quotation.preferredMovingDate || "",
          originAddress1: quotation.originAddress1 || "",
          originAddress2: quotation.originAddress2 || "",
          originCity: quotation.originCity || "",
          originState: quotation.originState || "",
          originPincode: quotation.originPincode || "",
          destinationAddress1: quotation.destinationAddress1 || "",
          destinationAddress2: quotation.destinationAddress2 || "",
          destinationCity: quotation.destinationCity || "",
          destinationState: quotation.destinationState || "",
          destinationPincode: quotation.destinationPincode || "",
        });
        const loadedRows = (items || []).map((item, index) =>
          applyQuotationValidationToRow({
            id: index + 1,
            item: item.itemName || "",
            quantity: String(item.quantity ?? ""),
            cftPerItem: item.cftPerItem ?? "",
            totalCft: item.totalCft ?? 0,
            quantityError: "",
            cftError: "",
            sourceMatched: !!item.matched,
            normalizedItem: item.normalizedItemName || "",
            notes: item.notes || "",
            matchStatus: item.matchStatus || (item.matched ? "Matched" : "Unmatched"),
          })
        );
        setRows(loadedRows.length ? loadedRows : [createEmptyRow(1)]);
        setNextId((loadedRows.length || 1) + 1);
        const v = quotation.vehicles;
        setVehicles(Array.isArray(v) && v.length ? [...v] : [""]);
      } catch (err) {
        toaster(err?.response?.data?.message || "Failed to load quotation", { type: "error" });
      } finally {
        setLoadingQuotation(false);
      }
    };
    loadQuotation();
  }, [isEditing, quotationId]);

  const applyImportedData = (importData, mode) => {
    const importedRows = (importData.items || []).map((item, idx) =>
      buildRowFromImportItem(item, nextId + idx)
    );

    setRows((prev) => {
      if (mode === "append") {
        const baseRows = prev.filter((r) => r.item || r.quantity || r.cftPerItem);
        const merged = [...baseRows, ...importedRows];
        return merged.length ? merged : [createEmptyRow(nextId + importedRows.length)];
      }
      return importedRows.length ? importedRows : [createEmptyRow(nextId + importedRows.length)];
    });
    setNextId((prev) => prev + importedRows.length + 1);
    setVehicles((prev) => {
      const incoming = importData.vehicles || [];
      if (mode === "append") {
        const merged = [
          ...prev.map((x) => String(x).trim()).filter(Boolean),
          ...incoming.map((x) => String(x).trim()).filter(Boolean),
        ];
        const unique = [...new Set(merged)];
        return unique.length ? unique : [""];
      }
      return incoming.length ? incoming.map((x) => String(x)) : [""];
    });
    setCustomer((prev) => ({
      ...prev,
      ...importData.customer,
    }));
    setWarnings(importData.warnings || []);
    setImportSummary(importData.summary || { extractedRows: 0, matchedItems: 0, unmatchedItems: 0 });
  };

  const handleImportData = async () => {
    if (!file) {
      setWarnings(["Please select an Excel file before importing"]);
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post("cft/import-excel", formData);
      const importData = response.data;
      if (!importData.success) {
        setWarnings([importData.message || "Failed to import Excel file"]);
        return;
      }

      if (hasData) {
        setPendingImportData(importData);
        setConfirmOpen(true);
        return;
      }
      applyImportedData(importData, "replace");
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.response?.data?.error || "Failed to import Excel file";
      setWarnings([message]);
    }
  };

  const clearImportedData = () => {
    setFile(null);
    setWarnings([]);
    setImportSummary({ extractedRows: 0, matchedItems: 0, unmatchedItems: 0 });
  };

  const handleCustomerChange = (key, value) => {
    setCustomer((prev) => ({ ...prev, [key]: value }));
  };

  const summary = useMemo(() => {
    const selectedRows = rows.filter((row) => !!row.item);
    return {
      totalItemsCount: selectedRows.length,
      totalQuantity: getTotalQuantity(rows),
      grandTotal: calculateGrandTotal(rows),
    };
  }, [rows]);

  const truckSuggestion = useMemo(() => suggestTruckFromCft(summary.grandTotal), [summary.grandTotal]);

  if (loadingQuotation) {
    return (
      <Box style={{ marginLeft: "130px", marginTop: "120px" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container>
      <Grid item lg={12} container>
        <Box className={classes.introBox}>
          <Typography variant="h3" component="h1" className={classes.primaryText}>
            {isEditing ? "Edit Quotation" : "New Quotation"}
          </Typography>
          <Typography variant="h6" component="span" className={classes.secondaryText}>
            Add items and quantities to estimate total cubic feet
          </Typography>
          {quotationId && (
            <Box style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => history.push(`/quotations/${quotationId}/pricing`)}
              >
                Quotation pricing &amp; PDF
              </Button>
              <Button
                variant="outlined"
                color="default"
                size="small"
                startIcon={<LinkIcon />}
                onClick={copyCustomerIntakeLink}
              >
                Copy customer intake link
              </Button>
            </Box>
          )}
        </Box>
      </Grid>

      <Grid item lg={12}>
        <Paper className={classes.sectionCard} elevation={1}>
          <Typography variant="h6">Customer Details</Typography>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item md={4} xs={12}>
              <TextField fullWidth label="Customer Name" variant="outlined" size="small" value={customer.customerName} onChange={(e) => handleCustomerChange("customerName", e.target.value)} required />
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField fullWidth label="Email" variant="outlined" size="small" value={customer.email} onChange={(e) => handleCustomerChange("email", e.target.value)} />
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField fullWidth label="Phone Number" variant="outlined" size="small" value={customer.phoneNumber} onChange={(e) => handleCustomerChange("phoneNumber", e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField fullWidth label="Company Name" variant="outlined" size="small" value={customer.companyName} onChange={(e) => handleCustomerChange("companyName", e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField fullWidth label="Preferred Moving Date" variant="outlined" size="small" value={customer.preferredMovingDate} onChange={(e) => handleCustomerChange("preferredMovingDate", e.target.value)} />
            </Grid>
            <Grid item md={12} xs={12}>
              <Typography variant="subtitle1">Origin Details</Typography>
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField fullWidth label="Address Line 1" variant="outlined" size="small" value={customer.originAddress1} onChange={(e) => handleCustomerChange("originAddress1", e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField fullWidth label="Address Line 2" variant="outlined" size="small" value={customer.originAddress2} onChange={(e) => handleCustomerChange("originAddress2", e.target.value)} />
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField fullWidth label="City" variant="outlined" size="small" value={customer.originCity} onChange={(e) => handleCustomerChange("originCity", e.target.value)} />
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField fullWidth label="State" variant="outlined" size="small" value={customer.originState} onChange={(e) => handleCustomerChange("originState", e.target.value)} />
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField fullWidth label="Pincode" variant="outlined" size="small" value={customer.originPincode} onChange={(e) => handleCustomerChange("originPincode", e.target.value)} />
            </Grid>

            <Grid item md={12} xs={12}>
              <Typography variant="subtitle1">Destination Details</Typography>
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField fullWidth label="Address Line 1" variant="outlined" size="small" value={customer.destinationAddress1} onChange={(e) => handleCustomerChange("destinationAddress1", e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField fullWidth label="Address Line 2" variant="outlined" size="small" value={customer.destinationAddress2} onChange={(e) => handleCustomerChange("destinationAddress2", e.target.value)} />
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField fullWidth label="City" variant="outlined" size="small" value={customer.destinationCity} onChange={(e) => handleCustomerChange("destinationCity", e.target.value)} />
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField fullWidth label="State" variant="outlined" size="small" value={customer.destinationState} onChange={(e) => handleCustomerChange("destinationState", e.target.value)} />
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField fullWidth label="Pincode" variant="outlined" size="small" value={customer.destinationPincode} onChange={(e) => handleCustomerChange("destinationPincode", e.target.value)} />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item lg={12}>
        <Paper className={classes.sectionCard} elevation={1}>
          <Typography variant="h6">Vehicles</Typography>
          <Typography variant="body2" color="textSecondary">
            Listed separately from household/office inventory. No quantity or CFT — not included in totals below.
          </Typography>
          <Grid container spacing={1} style={{ marginTop: 8 }}>
            {vehicles.map((v, idx) => (
              <Grid item xs={12} key={`vehicle-${idx}`}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item md={10} xs={9}>
                    <TextField
                      fullWidth
                      label={`Vehicle ${idx + 1}`}
                      variant="outlined"
                      size="small"
                      value={v}
                      placeholder="Make, model, registration no., etc."
                      onChange={(e) => {
                        const next = [...vehicles];
                        next[idx] = e.target.value;
                        setVehicles(next);
                      }}
                    />
                  </Grid>
                  <Grid item md={2} xs={3}>
                    <IconButton
                      aria-label="remove vehicle"
                      onClick={() =>
                        setVehicles(
                          vehicles.filter((_, i) => i !== idx).length
                            ? vehicles.filter((_, i) => i !== idx)
                            : [""]
                        )
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
          <Button
            style={{ marginTop: 8 }}
            size="small"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setVehicles([...vehicles, ""])}
          >
            Add vehicle
          </Button>
        </Paper>
      </Grid>

      <Grid item lg={12}>
        <Paper className={classes.sectionCard} elevation={1}>
          <Typography variant="h6">Excel Import</Typography>
          <Typography variant="body2" color="textSecondary">
            Recognized items will auto-fill CFT. Unknown items will be imported with blank CFT.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please review imported rows before using the total.
          </Typography>
          <div className={classes.importActions}>
            <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}>
              Upload Excel File
              <input hidden type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </Button>
            <Button variant="contained" color="primary" onClick={handleImportData}>Import Data</Button>
            <Button variant="outlined" onClick={clearImportedData}>Clear Imported Data</Button>
            <Typography variant="body2" style={{ alignSelf: "center" }}>
              {file ? `Selected: ${file.name}` : "No file selected"}
            </Typography>
          </div>
          <Box style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Chip label={`Rows Extracted: ${importSummary.extractedRows}`} />
            <Chip label={`Matched: ${importSummary.matchedItems}`} color="primary" />
            <Chip label={`Unmatched: ${importSummary.unmatchedItems}`} color="secondary" />
          </Box>
          {warnings.length > 0 && (
            <Box className={classes.warningPanel}>
              <Typography variant="subtitle2"><WarningIcon fontSize="small" style={{ verticalAlign: "middle" }} /> Import Warnings</Typography>
              {warnings.map((warning, idx) => (
                <Typography key={idx} variant="body2">{warning}</Typography>
              ))}
            </Box>
          )}
        </Paper>
      </Grid>

      <Grid item lg={12} className={classes.actionBar}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleOutlineIcon />}
          onClick={addRow}
        >
          Add Row
        </Button>
        <Button variant="outlined" startIcon={<ReplayIcon />} onClick={resetRows}>
          Reset
        </Button>
        <Button variant="outlined" color="secondary" startIcon={<FunctionsIcon />} onClick={calculateAllRows}>
          Calculate
        </Button>
        <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={saveQuotation}>
          {isEditing ? "Update Quotation" : "Save Quotation"}
        </Button>
      </Grid>

      <Grid item lg={12}>
        <Box className={classes.tableWrap}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>CFT per Item</TableCell>
                  <TableCell>Total CFT</TableCell>
                  <TableCell>Match Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    id={`quotation-row-${row.id}`}
                    className={rowNeedsManualCftHighlight(row) ? classes.rowNeedsManualCft : undefined}
                  >
                    <TableCell>
                      <TextField
                        className={classes.itemInput}
                        variant="outlined"
                        size="small"
                        value={row.item}
                        onChange={(e) => handleItemChange(row.id, e.target.value)}
                        placeholder="Search item"
                        inputProps={{ list: "cft-item-options" }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        className={classes.quantityInput}
                        variant="outlined"
                        size="small"
                        value={row.quantity}
                        onChange={(e) => handleQuantityChange(row.id, e.target.value)}
                        placeholder="0"
                        error={!!row.quantityError}
                        helperText={row.quantityError || " "}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        className={classes.cftInput}
                        variant="outlined"
                        size="small"
                        value={row.cftPerItem}
                        onChange={(e) => handleCftChange(row.id, e.target.value)}
                        placeholder="Blank if unknown"
                        error={!!row.cftError}
                        helperText={row.cftError || " "}
                      />
                    </TableCell>
                    <TableCell>{formatCft(row.totalCft)}</TableCell>
                    <TableCell>{row.matchStatus}</TableCell>
                    <TableCell align="center">
                      <IconButton onClick={() => removeRow(row.id)} aria-label="remove row">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <datalist id="cft-item-options">
            {ITEM_CFT_OPTIONS.map((itemName) => (
              <option key={itemName} value={itemName} />
            ))}
          </datalist>

          <Card className={classes.summaryCard}>
            <CardContent>
              <Typography variant="h6">Summary</Typography>
              <Typography variant="body1">Total Items Count: {summary.totalItemsCount}</Typography>
              <Typography variant="body1">Total Quantity: {summary.totalQuantity}</Typography>
              <Typography variant="body1">Grand Total CFT: {formatCft(summary.grandTotal)}</Typography>
              {truckSuggestion.label ? (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  Suggested truck: {truckSuggestion.label} (saved with the quotation; load line autofills if empty)
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Box>
      </Grid>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Import Existing Data Found</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Existing customer/table data is present. Do you want to replace current data or append imported rows?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              if (pendingImportData) applyImportedData(pendingImportData, "append");
              setConfirmOpen(false);
              setPendingImportData(null);
            }}
            color="default"
          >
            Append
          </Button>
          <Button
            onClick={() => {
              if (pendingImportData) applyImportedData(pendingImportData, "replace");
              setConfirmOpen(false);
              setPendingImportData(null);
            }}
            color="primary"
            variant="contained"
          >
            Replace
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default CftCalculatorPage;
